import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import GoalForm from "@pages/Program/components/goal/GoalForm";
import { GoalPayload } from "@shared/interfaces/ProgramFiles";
import { toastService } from "@services/ToastService";
import useHandleSaveGoal from "@pages/Program/components/goal/utils/useHandleSaveGoal";
import { useGoal } from "@pages/Program/components/goal/utils/useGoal";
import GoalFormSkeleton from "@pages/Program/components/goal/skeleton/GoalFormSkeleton";
import Loader from "@shared/components/Loader";

interface EditGoalProps {
  isOpen: boolean;
  onClose: () => void;
  goalId: string;
  fetchGoals: () => void;
  startDate: string;
  endDate: string;
}

const EditGoal: React.FC<EditGoalProps> = ({
  isOpen,
  onClose,
  goalId,
  fetchGoals,
  startDate,
  endDate,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { programId } = useParams();
  const { getGoalById } = useGoal();
  const [dataFetching, setDataFetching] = useState(true);

  const methods = useForm<GoalPayload>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      project_ids: [],
      projects: [],
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = methods;

  const handleSaveGoal = useHandleSaveGoal({ goalId }, methods);

  useEffect(() => {
    const fetchGoalData = async () => {
      setLoading(true);
      setDataFetching(true);
      if (!goalId) return;

      try {
        const goal = await getGoalById(goalId);
        if (!goal) {
          console.error("Failed to load goal data");
          return;
        }

        reset({
          name: goal.name,
          description: goal.description,
          start_date: goal.start_date,
          end_date: goal.end_date,
          projects: goal.projects,
        });

        setTimeout(() => {
          setDataFetching(false);
        }, 2000);
      } catch (error) {
        console.error("Error fetching goal data:", error);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 250);
      }
    };

    if (isOpen) fetchGoalData();
  }, [goalId, isOpen, reset]);

  const onSubmit = async (values: GoalPayload) => {
    if (!isValid) return;
    setSubmitting(true);

    try {
      if (!programId) return;

      const payload: GoalPayload = {
        ...values,
        reporting_frequency: null,
        program_id: programId,
      };

      await handleSaveGoal(payload);
      fetchGoals();
      onClose();
    } catch (error) {
      console.error("Failed to update goal", error);
      toastService.error("Failed to update goal");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-2xl py-6 shadow-lg">
        <h2 className="text-lg font-semibold pb-4 mb-4 border-b border-[#e5e7eb] px-6 ">
          {loading ? "Loading..." : "Edit goal"}
        </h2>

        {loading ? (
          <GoalFormSkeleton />
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="px-6">
              <GoalForm
                dataLoading={dataFetching}
                startDate={startDate}
                endDate={endDate}
              />
              <div className="flex justify-end mt-6 gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100 text-sm 2xl:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isValid || submitting}
                  className="px-4 py-2 bg-blue-600 text-white text-sm 2xl:text-base rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <Loader color="#fff" />
                  ) : (
                    <span>Update Goal</span>
                  )}
                </button>
              </div>
            </form>
          </FormProvider>
        )}
      </div>
    </div>
  );
};

export default EditGoal;
